import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationsApiAdapter } from '../adapters/NotificationsApiAdapter';
import { useAuthStore } from '@shared/stores/authStore';

const PER_PAGE = 100;

function isSameUserId(
  notificationUserId: number | string | undefined,
  currentUserId: number | string | undefined
): boolean {
  if (currentUserId == null || notificationUserId == null) return false;
  return Number(notificationUserId) === Number(currentUserId);
}

/**
 * Marca como leídas todas las notificaciones no leídas del usuario actual
 * (paginando el listado; sin endpoint masivo en API).
 */
export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new NotificationsApiAdapter(httpClient);
  const userId = useAuthStore((state) => state.user?.id);

  return useMutation({
    mutationFn: async () => {
      if (userId == null) throw new Error('No user');

      const filters = adapter.buildFiltersForReadFilter('unread');
      if (!filters) throw new Error('No filters');

      const ids: number[] = [];
      let page = 1;
      let lastPage = 1;

      do {
        const res = await adapter.getNotifications({
          page,
          per_page: PER_PAGE,
          sortColumn: 'id',
          sortDirection: 'desc',
          filters,
        });

        for (const n of res.data) {
          if (isSameUserId(n.user_id, userId) && !n.archived_at) {
            ids.push(n.id);
          }
        }

        lastPage = res.meta.lastPage;
        page += 1;
      } while (page <= lastPage);

      const BATCH = 25;
      for (let i = 0; i < ids.length; i += BATCH) {
        const batch = ids.slice(i, i + BATCH);
        await Promise.all(batch.map((id) => adapter.markAsRead(id)));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
